import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutGrid, Ticket, Users, BarChart3, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

const INTERFACE_ICONS = {
  'ticket_creator': Ticket,
  'process_owner': Users,
  'cao': BarChart3,
};

const INTERFACE_COLORS = {
  'ticket_creator': 'text-blue-600 bg-blue-50',
  'process_owner': 'text-emerald-600 bg-emerald-50',
  'cao': 'text-purple-600 bg-purple-50',
};

const InterfaceSwitcher = ({ user, currentInterface }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Show for caredesk owners OR registered staff with switch capability
  // More resilient check - show if either flag is true AND interfaces are available
  const canSwitch = (user?.can_switch_interface || user?.is_registered_staff) && 
                    user?.available_interfaces?.length > 0;
  
  if (!canSwitch) {
    return null;
  }

  const handleSwitch = (interfaceData) => {
    setIsOpen(false);
    navigate(interfaceData.route);
  };

  // Determine current interface based on route
  const getCurrentInterface = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'ticket_creator';
    if (path === '/process-owner' || path === '/resolver') return 'process_owner';
    if (path === '/cao' || path === '/analytics' || path === '/users') return 'cao';
    return currentInterface || 'ticket_creator';
  };

  const current = getCurrentInterface();
  const CurrentIcon = INTERFACE_ICONS[current] || LayoutGrid;
  const currentData = user.available_interfaces.find(i => i.role === current);
  
  // Determine the label for the footer
  const getUserLabel = () => {
    if (user?.is_caredesk_owner) {
      return `Caredesk Owner: ${user.name}`;
    } else if (user?.is_registered_staff) {
      return `Staff: ${user.name}`;
    }
    return user.name;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="flex items-center gap-1.5 sm:gap-2 bg-white border-slate-200 hover:bg-slate-50 shadow-sm"
          data-testid="interface-switcher-btn"
        >
          <div className={`p-1 sm:p-1.5 rounded ${INTERFACE_COLORS[current] || 'bg-slate-100'}`}>
            <CurrentIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </div>
          <span className="font-medium text-slate-700 hidden sm:inline text-sm">
            {currentData?.name || 'Dashboard'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
          Switch Interface
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {user.available_interfaces.map((interfaceData) => {
          const Icon = INTERFACE_ICONS[interfaceData.role] || LayoutGrid;
          const isActive = interfaceData.role === current;
          
          return (
            <DropdownMenuItem
              key={interfaceData.role}
              onClick={() => handleSwitch(interfaceData)}
              className={`flex items-center gap-3 py-3 cursor-pointer ${isActive ? 'bg-orange-50' : ''}`}
              data-testid={`switch-to-${interfaceData.role}`}
            >
              <div className={`p-2 rounded-lg ${INTERFACE_COLORS[interfaceData.role] || 'bg-slate-100'}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <p className={`font-medium ${isActive ? 'text-[#E97832]' : 'text-slate-700'}`}>
                  {interfaceData.name}
                </p>
                <p className="text-xs text-slate-500">{interfaceData.description}</p>
              </div>
              {isActive && (
                <div className="h-2 w-2 rounded-full bg-[#E97832]" />
              )}
            </DropdownMenuItem>
          );
        })}
        
        <DropdownMenuSeparator />
        <div className="px-2 py-2">
          <p className="text-xs text-slate-400 text-center">
            {getUserLabel()}
          </p>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default InterfaceSwitcher;
